import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';

interface StorageStatus {
  active: boolean; tier: string | null; limit: number | null; used: number;
  subscriptionId: string | null; cancelAtPeriodEnd: boolean; currentPeriodEnd: number | null;
}
interface StoragePlanTier { priceId: string; productName: string; amount: number; currency: string; interval: string; limit: number | null }

function money(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}
function fmtDate(epoch: number | null) {
  return epoch ? new Date(epoch * 1000).toLocaleDateString() : '';
}

/** Secure-storage tier catalog + current-plan state. Lives on the Subscriptions page. */
export function StorageTiers() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['storage-status'], queryFn: () => api.get<StorageStatus>('/storage/status') });
  const plans = useQuery({ queryKey: ['storage-plans'], queryFn: () => api.get<StoragePlanTier[]>('/storage/plans') });
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const active = status.data?.active;
  const currentLimit = status.data?.limit ?? null;
  const pendingCancel = status.data?.cancelAtPeriodEnd;

  async function subscribe(priceId: string) {
    setBusy(priceId); setErr(null);
    try {
      const res = await api.post<{ checkoutUrl: string }>('/storage/subscribe', { priceId });
      if (res.checkoutUrl) window.location.href = res.checkoutUrl;
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not start checkout');
    } finally { setBusy(null); }
  }

  async function setCancel(cancel: boolean) {
    setBusy('cancel'); setErr(null);
    try {
      await api.post('/storage/cancel', { cancel });
      qc.invalidateQueries({ queryKey: ['storage-status'] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update the storage plan');
    } finally { setBusy(null); }
  }

  return (
    <div>
      <div className="section-title" style={{ marginTop: 32 }}>Secure EFT storage</div>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        {active && status.data?.tier
          ? <>// Current plan: <strong>{status.data.tier}</strong> · {status.data.used.toLocaleString()}{currentLimit != null ? ` / ${currentLimit.toLocaleString()}` : ''} EFTs stored · tiered by number of EFTs</>
          : <>// Store every EFT securely under your account and search/download it any time — tiered by number of EFTs, billed monthly.</>}
      </p>

      {active && (
        pendingCancel ? (
          <div className="banner-warn" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, maxWidth: 640 }}>
            <span>Storage cancels {status.data?.currentPeriodEnd ? `on ${fmtDate(status.data.currentPeriodEnd)}` : 'at the end of the billing cycle'} — no refund for the current period.</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setCancel(false)} disabled={busy === 'cancel'}>Keep storage</button>
          </div>
        ) : (
          <button className="btn btn-ghost btn-sm cancel-btn" style={{ marginBottom: 6 }} onClick={() => setCancel(true)} disabled={busy === 'cancel'}>
            {busy === 'cancel' ? 'Canceling…' : 'Cancel storage plan'}
          </button>
        )
      )}

      {err && <p className="cart-error">{err}</p>}
      {plans.isLoading ? (
        <p className="loading-screen">// loading storage plans…</p>
      ) : (plans.data ?? []).length === 0 ? (
        <div className="panel empty"><h3>No storage plans available</h3><p>Storage tiers are configured in Stripe.</p></div>
      ) : (
        <div className="plan-grid">
          {plans.data!.map((p) => {
            const isCurrent = currentLimit != null && p.limit === currentLimit;
            const isUpgrade = currentLimit != null && p.limit != null && p.limit > currentLimit;
            return (
              <div key={p.priceId} className={`plan-card${isCurrent ? ' is-current' : ''}`}>
                <div className="plan-top">
                  <h3>{p.productName}</h3>
                  {isCurrent && <span className="pill pill-scan">Current</span>}
                </div>
                <p className="plan-desc">{p.limit != null ? `Store up to ${p.limit.toLocaleString()} EFTs` : 'Unlimited storage'}</p>
                <div className="plan-price">{money(p.amount, p.currency)}<span className="plan-interval">/{p.interval}</span></div>
                <button className="btn btn-primary" disabled={busy === p.priceId || isCurrent} onClick={() => subscribe(p.priceId)}>
                  {isCurrent ? 'Current plan' : busy === p.priceId ? 'Starting…' : active ? (isUpgrade ? 'Upgrade →' : 'Switch →') : 'Subscribe →'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
